const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const initSlider = (slider) => {
  const section = slider.closest('.home-reviews');
  const viewport = slider.querySelector('.home-reviews__viewport');
  const track = slider.querySelector('[data-slider-track]');
  const slides = Array.from(slider.querySelectorAll('[data-slider-slide]'));
  const previous = section?.querySelector('[data-slider-previous]');
  const next = section?.querySelector('[data-slider-next]');
  if (!viewport || !track || slides.length < 1 || !previous || !next) {
    return;
  }

  let currentIndex = 0;
  let currentOffset = 0;
  let dragStartX = 0;
  let dragDelta = 0;
  let dragStartedAt = 0;
  let isDragging = false;

  const maxOffset = () => Math.max(0, track.scrollWidth - viewport.clientWidth);

  const visibleCount = () => {
    const firstSlide = slides[0];

    if (!firstSlide) {
      return 1;
    }

    const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
    const slideWidth = firstSlide.getBoundingClientRect().width;
    return Math.max(1, Math.round((viewport.clientWidth + gap) / (slideWidth + gap)));
  };

  const maxIndex = () => Math.max(0, slides.length - visibleCount());

  const offsetForIndex = (index) => Math.min(slides[index]?.offsetLeft || 0, maxOffset());

  const render = ({ instant = false } = {}) => {
    currentIndex = clamp(currentIndex, 0, maxIndex());
    currentOffset = offsetForIndex(currentIndex);
    track.classList.toggle('is-instant', instant);
    track.style.transform = `translate3d(${-currentOffset}px, 0, 0)`;

    previous.disabled = currentIndex === 0;
    next.disabled = currentIndex === maxIndex();
    slider.classList.toggle('is-static', maxIndex() === 0);

    slides.forEach((slide, index) => {
      const isVisible = index >= currentIndex && index < currentIndex + visibleCount();
      slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    });

    if (instant) {
      window.requestAnimationFrame(() => track.classList.remove('is-instant'));
    }
  };

  const goTo = (index, options) => {
    currentIndex = clamp(index, 0, maxIndex());
    render(options);
  };

  previous.addEventListener('click', () => goTo(currentIndex - 1));
  next.addEventListener('click', () => goTo(currentIndex + 1));

  slider.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    event.preventDefault();
    goTo(currentIndex + (event.key === 'ArrowRight' ? 1 : -1), { instant: true });
  });

  viewport.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    isDragging = true;
    dragStartX = event.clientX;
    dragStartedAt = performance.now();
    dragDelta = 0;
    track.classList.add('is-dragging');
    viewport.setPointerCapture(event.pointerId);
  });

  viewport.addEventListener('pointermove', (event) => {
    if (!isDragging) {
      return;
    }

    dragDelta = event.clientX - dragStartX;
    const rawOffset = currentOffset - dragDelta;
    const boundedOffset = rawOffset < 0
      ? rawOffset * 0.22
      : maxOffset() + (rawOffset - maxOffset()) * 0.22;
    const displayOffset = rawOffset < 0 || rawOffset > maxOffset() ? boundedOffset : rawOffset;
    track.style.transform = `translate3d(${-displayOffset}px, 0, 0)`;
  });

  const endDrag = (event) => {
    if (!isDragging) {
      return;
    }

    isDragging = false;
    track.classList.remove('is-dragging');

    if (viewport.hasPointerCapture(event.pointerId)) {
      viewport.releasePointerCapture(event.pointerId);
    }

    const elapsed = Math.max(performance.now() - dragStartedAt, 1);
    const velocity = Math.abs(dragDelta) / elapsed;
    const shouldMove = Math.abs(dragDelta) > 64 || velocity > 0.45;
    goTo(currentIndex + (shouldMove ? (dragDelta < 0 ? 1 : -1) : 0));
  };

  viewport.addEventListener('pointerup', endDrag);
  viewport.addEventListener('pointercancel', endDrag);

  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(() => render({ instant: true }));
    observer.observe(viewport);
  }

  render({ instant: true });
};

export default function initHomeTestimonials() {
  document.querySelectorAll('[data-testimonial-slider]').forEach(initSlider);
}
